import { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Package } from '@phosphor-icons/react'
import { useFormat, useI18n, type MessageKey } from '@/i18n'
import { formatGrams } from '@/lib/weight'
import {
  Badge,
  Card,
  CardBody,
  EmptyState,
  PickerSheet,
  Skeleton,
  StatusPill,
  type Tone,
} from '@/ui'
import { GateScreen } from '../components/GateScreen'
import { useCodeLabel } from '../components/labels'
import { scrubSyncedMoney } from '../data/actions'
import { useGate } from '../data/useGate'
import { deliveriesOf, type DayDelivery } from '../model/day'

const SYNC_LABEL: Record<string, { key: MessageKey; tone: Tone }> = {
  PENDING: { key: 'gate.list.waiting', tone: 'neutral' },
  SENDING: { key: 'gate.list.sending', tone: 'info' },
  WAIT_FOR_PARENT: { key: 'gate.list.waiting', tone: 'neutral' },
  APPLIED: { key: 'gate.list.received', tone: 'ok' },
  DUPLICATE: { key: 'gate.list.received', tone: 'ok' },
  HELD: { key: 'gate.list.held', tone: 'warn' },
  ERROR: { key: 'gate.list.problem', tone: 'danger' },
}

const PAYOUT_LABEL: Record<string, MessageKey> = {
  PENDING_APPROVAL: 'gate.pay.waiting',
  READY: 'gate.pay.waiting',
  SUBMITTING: 'gate.pay.sent',
  PROCESSING: 'gate.pay.sent',
  PROCESSED: 'gate.pay.paid',
  FAILED: 'gate.pay.officeWillCall',
  REVERSED: 'gate.pay.officeWillCall',
  CANCELLED: 'gate.pay.officeWillCall',
}

/** Deliveries of the day on this phone, with what the server said (F4.10, F4.9-l). No amounts. */
export function DeliveryListPage() {
  const { t } = useI18n()
  const fmt = useFormat()
  const navigate = useNavigate()
  const g = useGate()
  const code = useCodeLabel()
  const sessions = useMemo(
    () => [...g.sessions].sort((a, b) => (a.opened_at < b.opened_at ? 1 : -1)),
    [g.sessions],
  )
  const [picked, setPicked] = useState<string | null>(null)
  const sessionId = picked ?? g.app?.id ?? sessions[0]?.id ?? null
  const list = useMemo(
    () => (sessionId ? deliveriesOf(g.outbox, sessionId) : []),
    [g.outbox, sessionId],
  )
  const drafts = g.drafts.filter((x) => x.data.session_id === sessionId)

  useEffect(() => {
    void scrubSyncedMoney()
  }, [list.length])

  if (!g.ready) return <Skeleton className="h-64 w-full" />

  return (
    <GateScreen title="gate.list.title">
      {sessions.length > 1 && (
        <PickerSheet
          title={t('gate.list.day')}
          value={sessionId}
          onChange={setPicked}
          options={sessions.map((s) => ({
            value: s.id,
            label: s.code,
            description: `${fmt.date(s.opened_at)} · ${s.capture_mode === 'PAPER' ? t('gate.paper.short') : s.operator_name}`,
            meta: <Badge tone={s.status === 'OPEN' ? 'ok' : 'neutral'}>{s.status}</Badge>,
          }))}
        />
      )}

      {drafts.map((x) => (
        <button
          key={x.id}
          type="button"
          onClick={() =>
            navigate(x.data.capture_mode === 'APP' ? '/m/gate/delivery/new' : '/m/gate/paper')
          }
          className="rounded-card border border-info/40 bg-info-wash px-3 py-2.5 text-left"
        >
          <div className="flex items-center justify-between gap-2">
            <span className="font-semibold">{x.data.farmer.name}</span>
            <Badge tone="info">{t('gate.list.inProgress')}</Badge>
          </div>
          <div className="font-mono text-[13px] text-muted">{x.data.main_lot.code}</div>
        </button>
      ))}

      {list.length === 0 && drafts.length === 0 ? (
        <EmptyState icon={<Package />} title={t('gate.list.empty')} compact />
      ) : (
        <ul className="flex flex-col gap-2">
          {list.map((x) => (
            <DeliveryRow key={x.command_id} x={x} code={code} />
          ))}
        </ul>
      )}
    </GateScreen>
  )
}

function DeliveryRow({ x, code }: { x: DayDelivery; code: (c: string) => string }) {
  const { t } = useI18n()
  const fmt = useFormat()
  const g = useGate()
  const farmer = g.roster?.farmers.find((f) => f.user_id === x.farmer_id)
  const sync = SYNC_LABEL[x.sync] ?? {
    key: 'gate.list.waiting' as MessageKey,
    tone: 'neutral' as Tone,
  }
  return (
    <li>
      <Card>
        <CardBody className="flex flex-col gap-2 py-3">
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0">
              <div className="truncate font-semibold">{farmer?.name ?? x.farmer_id}</div>
              <div className="text-[12px] text-muted">
                {fmt.time(x.at)}
                {x.slip_no ? ` · ${t('gate.list.slip', { no: x.slip_no })}` : ''}
              </div>
            </div>
            <div className="flex flex-col items-end gap-1">
              {x.kind === 'VOIDED' ? (
                <StatusPill status="VOID" label={code(x.void_reason ?? 'VOID')} />
              ) : (
                <StatusPill status={sync.tone === 'ok' ? 'OK' : x.sync} label={t(sync.key)} />
              )}
              {x.disputed && <Badge tone="warn">{t('gate.list.disputed')}</Badge>}
            </div>
          </div>
          {x.kind === 'COMMITTED' &&
            x.lots.map((l) => (
              <div key={l.id} className="rounded-chip bg-paper-sunk px-2.5 py-2">
                <div className="flex flex-wrap items-baseline justify-between gap-2">
                  <span className="font-mono text-[14px]">{l.code}</span>
                  <span className="text-[13px]">
                    {t(l.stream === 'MAIN' ? 'gate.lot.goodBags' : 'gate.lot.redBags', {
                      count: l.bag_count,
                    })}{' '}
                    · <span className="font-mono">{formatGrams(l.net_g)}</span>
                  </span>
                </div>
                <div className="mt-1 flex flex-wrap gap-1">
                  {l.hold && l.hold !== 'NONE' && (
                    <Badge tone="warn">{t('gate.list.onHold')}</Badge>
                  )}
                  {l.hold_reasons.map((r) => (
                    <Badge key={r} tone="warn">
                      {code(r)}
                    </Badge>
                  ))}
                  {l.payout_status && (
                    <Badge
                      tone={
                        l.payout_status === 'PROCESSED'
                          ? 'ok'
                          : l.payout_status === 'FAILED' || l.payout_status === 'REVERSED'
                            ? 'danger'
                            : 'neutral'
                      }
                    >
                      {t(PAYOUT_LABEL[l.payout_status] ?? 'gate.pay.waiting')}
                      {l.utr ? ` · ${l.utr}` : ''}
                    </Badge>
                  )}
                </div>
              </div>
            ))}
          {x.sync === 'ERROR' && x.error && <p className="text-[13px] text-danger">{x.error}</p>}
        </CardBody>
      </Card>
    </li>
  )
}
